import PropTypes from "prop-types";

// Layouts
import DoubleInput from "@/Layouts/components/InputComponents/DoubleInput";
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import TierRowsItem from "@/Layouts/components/Tiers/TierRowsItem";

// MUI
import { Stack } from "@mui/material";

const getMinMaxRanges = (definition, type) => definition?.[type] || null;

const useDashedInputDefinitions = (data = [], inputRanges = null, dashed = false) => {
	return data.map((definition) => {
		const ranges = getMinMaxRanges(inputRanges, definition.type);
		return { ...definition, ...ranges, dashed };
	});
};

export default function MatrixRow(props) {
	const {
		index = 0,
		item = null,
		inputDataDefinitions = [
			{
				type: "Dollar",
				decimalsEditable: true,
				decimals: 2,
			},
		],
		inputRanges = {
			Dollar: {
				min: 0,
				max: 100000000,
			},
			Percent: {
				min: 0,
				max: 100,
			},
			Unit: {
				min: 0,
				max: 500,
			},
		},
		fromDisabled = false,
		toDisabled = false,
		spaces = {
			mainSection: 100,
			columns: 100,
			totalColumns: 1,
			totalRows: 1,
			separation: 5.5,
		},
		rowsErrors = [],
		columnsErrors = [],
		lastBonusError = false,
		readOnly = false,
		spacing = 0.8,
		mainContentProps = null,
		sxMainContent = {
			minWidth: "190px",
		},
		cellsProps = {
			justifyContent: "center",
			alignItems: "center",
		},
		sxCells = {
			minWidth: "100px",
		},
		onActionUpdate = () => {},
		onChangeType = () => {},
	} = props;

	const firstInputData = useDashedInputDefinitions(inputDataDefinitions, inputRanges, fromDisabled);
	const secondInputData = useDashedInputDefinitions(inputDataDefinitions, inputRanges, toDisabled);

	return (
		<TierRowsItem hasShadow={!(index % 2)} spaces={spaces}>
			<Stack direction={"row"} spacing={spacing}>
				<Stack {...mainContentProps} sx={sxMainContent}>
					<DoubleInput
						firstInput={{
							data: firstInputData,
							sx: { width: "100px" },
							defaultType: item?.from?.input || "Dollar",
							value: item?.from?.value || 0,
							error: Boolean(rowsErrors?.[index]?.from),
							disabled: fromDisabled,
							readOnly: true,
							onActionUpdate: (value) => {
								onActionUpdate?.(index, 0, "row", "from", value);
							},
							onChangeType: (_, input) => {
								onChangeType?.(index, 0, "row", "from", input);
							},
						}}
						secondInput={{
							readOnly,
							data: secondInputData,
							sx: { width: "100px" },
							defaultType: item?.to?.input || "Dollar",
							value: item?.to?.value || 0,
							error: Boolean(rowsErrors?.[index]?.to),
							disabled: toDisabled,
							onActionUpdate: (value) => {
								onActionUpdate?.(index, 0, "row", "to", value);
							},
							onChangeType: (_, input) => {
								onChangeType?.(index, 0, "row", "to", input);
							},
						}}
					/>
				</Stack>
				<Stack direction={"row"} spacing={spacing}>
					{item?.bonuses?.map((element, key) => {
						const itemKey = `cit_bonus_${key}`;
						const isBonusError =
							(key === item?.bonuses?.length - 1 && lastBonusError) || Boolean(columnsErrors?.[index]?.[key]?.["bonus"]);
						return (
							<Stack key={itemKey} {...cellsProps} sx={sxCells}>
								<InputFormExtension
									data={[
										{
											type: "Percent",
											min: -100,
											max: 100,
											decimalsEditable: true,
											decimals: 2,
										},
										{
											type: "Dollar",
											min: -100000000,
											max: 100000000,
											decimalsEditable: false,
											decimals: 0,
										},
									]}
									value={element?.bonus?.value || 0}
									defaultType={element?.bonus?.input || "Percent"}
									error={isBonusError}
									disabled={readOnly}
									onChangeType={(_, input) => {
										onChangeType?.(index, key, "row", "bonus", input);
									}}
									onActionUpdate={(value) => {
										onActionUpdate?.(index, key, "row", "bonus", value);
									}}
									sx={{
										width: "95px",
									}}
								/>
							</Stack>
						);
					})}
				</Stack>
			</Stack>
		</TierRowsItem>
	);
}
MatrixRow.propTypes = {
	index: PropTypes.number,
	item: PropTypes.object,
	inputDataDefinitions: PropTypes.array,
	inputRanges: PropTypes.object,
	fromDisabled: PropTypes.bool,
	toDisabled: PropTypes.bool,
	spaces: PropTypes.object,
	rowsErrors: PropTypes.array,
	columnsErrors: PropTypes.array,
	readOnly: PropTypes.bool,
	spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	mainContentProps: PropTypes.object,
	sxMainContent: PropTypes.object,
	cellsProps: PropTypes.object,
	sxCells: PropTypes.object,
	onActionUpdate: PropTypes.func,
	onChangeType: PropTypes.func,
};
